import React from 'react';
import StatusBadge from '../ui/StatusBadge';
import ProcSection from './ProcSection';
import { formatDecimal } from '../../utils/decimal';
import type { LandedCost, LandedCostIssue } from '../../types/Procurement';
import { recordLabelOr } from '../../utils/recordId';
import { useRecordRefs } from '../../utils/recordRefs';

export const LANDED_COST_ISSUES: Record<LandedCostIssue, string> = {
  GOODS_RATE_MISSING: 'The goods total has no exchange rate to the project currency, so it is left out. Add a rate to the order.',
  FREIGHT_RATE_MISSING: 'Freight has no exchange rate to the project currency, so it is left out.',
  CLEARING_RATE_MISSING: 'Local clearing has no exchange rate to the project currency, so it is left out.',
  SHIPMENT_COSTS_NOT_RECORDED: 'A linked shipment has no freight or clearing cost recorded yet.',
};

const BASIS: Record<string, string> = { cbm: 'by volume (CBM)', cartons: 'by cartons', equal: 'equally' };

/**
 * The landed cost of an order, in the project currency, worked out by the API.
 * A figure the API could not work out is null and shows as an em dash with the
 * reason beside it; nothing is guessed here. Money-only: the caller leaves this
 * panel out for someone who may not see money.
 */
const LandedCostPanel: React.FC<{ cost: LandedCost | null }> = ({ cost }) => {
  // Re-render when the record numbers load.
  useRecordRefs();
  if (!cost) {
    return (
      <ProcSection title="Landed cost">
        <p className="text-subtle">Landed cost is not available yet. It needs the project currency to be set and an exchange rate for the order.</p>
      </ProcSection>
    );
  }
  const rows: Array<[string, string | null]> = [
    ['Goods', cost.goods],
    [`Freight${cost.freight_source ? ` (from the ${cost.freight_source})` : ''}`, cost.freight],
    ['Local clearing', cost.clearing],
    ['Tax', cost.tax],
    ['Contingency', cost.contingency],
  ];
  const excluded = cost.excluded_shipments.draft + cost.excluded_shipments.cancelled;
  return (
    <ProcSection title="Landed cost" action={<StatusBadge variant={cost.complete ? 'success' : 'warning'}>{cost.complete ? 'Complete' : 'Incomplete'}</StatusBadge>}>
      <dl className="proc-kv">
        {rows.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt>{label}</dt>
            <dd>{formatDecimal(value, cost.currency)}</dd>
          </React.Fragment>
        ))}
        <dt className="proc-kv__total">Total</dt>
        <dd className="proc-kv__total">{formatDecimal(cost.total, cost.currency)}</dd>
      </dl>
      {cost.issues.length > 0 && (
        <ul className="proc-issues">
          {cost.issues.map(issue => <li key={issue}>{LANDED_COST_ISSUES[issue] ?? issue}</li>)}
        </ul>
      )}
      {cost.shipments.length > 0 && (
        <table className="proc-table proc-table--compact">
          <thead><tr><th>Shipment</th><th>Split</th><th>Freight share</th><th>Clearing share</th></tr></thead>
          <tbody>
            {cost.shipments.map(s => (
              <tr key={s.shipment_id}>
                <td>{recordLabelOr('shipment', s.shipment_id, s.shipment_id.slice(0, 8).toUpperCase())}</td>
                <td>{BASIS[s.basis] ?? s.basis}</td>
                <td>{formatDecimal(s.freight_converted ?? s.freight_share, s.freight_converted ? cost.currency : s.freight_currency)}</td>
                <td>{formatDecimal(s.clearing_share, cost.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {excluded > 0 && <p className="text-subtle">{excluded} draft or cancelled shipment{excluded === 1 ? ' is' : 's are'} not counted.</p>}
    </ProcSection>
  );
};

export default LandedCostPanel;
